import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"

export default class extends Controller {
  static targets = ["trigger", "form"]
  static values = {
    message: String,
    confirmLabel: String,
    cancelLabel: String,
    title: String,
    blocked: { type: Boolean, default: false }
  }

  connect() {
    this.popover = new bootstrap.Popover(this.triggerTarget, {
      html: true,
      title: () => this.popoverTitle(),
      content: () => this.buildContent(),
      trigger: "manual",
      placement: "left",
      sanitize: false,
      customClass: this.popoverClass()
    })

    this._onShown = () => {
      this._popoverEl = this.findPopoverElement()
    }
    this.triggerTarget.addEventListener("shown.bs.popover", this._onShown)

    this._outsideClick = (event) => {
      if (!this.element.contains(event.target) && !event.target.closest(".confirm-destroy-popover")) {
        this.hide()
      }
    }
    document.addEventListener("click", this._outsideClick)

    this._hideAll = () => this.hide()
    document.addEventListener("confirm-destroy-popover:hide-all", this._hideAll)

    this._popoverClick = (event) => {
      const actionEl = event.target.closest("[data-confirm-destroy-popover-action]")
      if (!actionEl || !this._popoverEl?.contains(actionEl)) return

      event.preventDefault()
      if (actionEl.dataset.confirmDestroyPopoverAction === "confirm") {
        this.confirm(event)
      } else {
        this.cancel(event)
      }
    }
    document.addEventListener("click", this._popoverClick)
  }

  disconnect() {
    this.triggerTarget.removeEventListener("shown.bs.popover", this._onShown)
    document.removeEventListener("click", this._outsideClick)
    document.removeEventListener("click", this._popoverClick)
    document.removeEventListener("confirm-destroy-popover:hide-all", this._hideAll)
    this.dispose()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.triggerTarget.disabled) return

    if (this.isOpen()) {
      this.hide()
      return
    }

    document.dispatchEvent(new CustomEvent("confirm-destroy-popover:hide-all"))
    this.popover.show()
  }

  confirm(event) {
    event.preventDefault()
    this.hide()
    if (this.hasFormTarget) this.formTarget.requestSubmit()
  }

  cancel(event) {
    event.preventDefault()
    this.hide()
  }

  hide() {
    if (this.popover) this.popover.hide()
    this._popoverEl = null
  }

  dispose() {
    if (this.popover) {
      this.popover.hide()
      this.popover.dispose()
    }
    this._popoverEl = null
  }

  isOpen() {
    const popoverEl = this.findPopoverElement()
    return popoverEl?.classList.contains("show")
  }

  findPopoverElement() {
    const id = this.triggerTarget.getAttribute("aria-describedby")
    return id ? document.getElementById(id) : null
  }

  popoverClass() {
    return this.blockedValue
      ? "confirm-destroy-popover confirm-destroy-popover--blocked"
      : "confirm-destroy-popover"
  }

  popoverTitle() {
    if (!this.hasTitleValue || this.titleValue === "") return ""

    return this.escapeHtml(this.titleValue)
  }

  buildContent() {
    const message = this.escapeHtml(this.messageValue)
    const cancelLabel = this.escapeHtml(this.cancelLabelValue)

    if (this.blockedValue) {
      return `
        <div class="confirm-destroy-popover__body">
          <p class="confirm-destroy-popover__message mb-2">${message}</p>
          <div class="d-flex gap-2 justify-content-end">
            <button type="button" class="btn btn-sm btn-outline-secondary" data-confirm-destroy-popover-action="cancel">${cancelLabel}</button>
          </div>
        </div>
      `
    }

    const confirmLabel = this.escapeHtml(this.confirmLabelValue)

    return `
      <div class="confirm-destroy-popover__body">
        <p class="confirm-destroy-popover__message mb-2">${message}</p>
        <div class="d-flex gap-2 justify-content-end">
          <button type="button" class="btn btn-sm btn-outline-secondary" data-confirm-destroy-popover-action="cancel">${cancelLabel}</button>
          <button type="button" class="btn btn-sm btn-soft-danger" data-confirm-destroy-popover-action="confirm">${confirmLabel}</button>
        </div>
      </div>
    `
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
